import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as store from "source/framework/customStore";
import { SaveDataObj } from "source/framework/saveData";
import { IRecordEducationEditItem } from "source/models/recordEducation/recordEducationEditItem";
import * as recordService from "source/models/services/recordService";
import * as storeActions from "source/models/store/storeActions";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IRecordEducationDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IRecordEducationDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    education: IRecordEducationEditItem;
}

export class RecordEducationDialog extends React.Component<IRecordEducationDialogProp, IRecordEducationDialogState> {
    private orgStateHash: string = "";

    constructor(props: IRecordEducationDialogProp) {
        super(props);
        this.state = {
            id: "",
            rowId: "",
            isNew: false,
            education: null,
            code: "",
            comment: ""
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => recordService.getRecordBasicEducationEdit(props.companyId, id), false);
        if (!result) return;
        const education = result.education;
        const state = {
            ...this.state,
            id: education.id,
            rowId: education.rowId,
            isNew: education.isNew(),
            education,
            code: education.code,
            comment: education.comment
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IRecordEducationDialogProp, prevState: IRecordEducationDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    getSaveDataFromState = (props: IRecordEducationDialogProp, state: IRecordEducationDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.NameMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => recordService.saveRecordBasicEducationEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordBasicEducationParameter, state.education.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.education) return null;
        return (
            <Dialog
                id="record-education-dialog"
                maxWidth="sm"
                title={state.isNew ? Translations.AddBasicEducation : (Translations.BasicEducation + ": " + state.education.code)}
                body={
                    <>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField type="text" name="code" title={Translations.Name} inputProps={{ maxLength: 100 }} disabled={false} value={state.code} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description}>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.education.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}